import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
// material
import {
    Container,
    Typography,
    Button,
    Box,
    Stack,
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import downIcon from "@iconify/icons-eva/arrow-down-outline";
import { Icon } from "@iconify/react";
// components
import Page from "../components/Page";
import backIcon from "@iconify/icons-eva/arrow-back-fill";
import ImageGallery from "react-image-gallery";
import Gallery from "src/css/Gallery.css";
import Loader from "src/components/loader/Loader";
import { AdminTable } from "src/components/dashboard/admin/utlis";
import filesize from "filesize";
import saveAs from "file-saver";
import JSZipUtils from "jszip-utils";
import DownloadButton from "src/components/dashboard/admin/utlis/DownloadButton";
import { editContent, clearEditContent } from "src/redux/slices/Content";
import { useDispatch, useSelector } from "react-redux";
import { get, post } from "src/services/content.service";
import { useTranslation } from "react-i18next";
import axios from "axios";
import InputTextDialog from "src/components/dashboard/admin/Content/InputTextDialog";
import ConfirmDialog from "src/components/dashboard/admin/Popup/ConfirmDialog";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { showAlert } from "src/redux/slices/AlertSlice";

// ----------------------------------------------------------------------

ContentDetail.propTypes = {
    endpoint: PropTypes.string.isRequired,
    altSlug: PropTypes.string,
};

export default function ContentDetail({ endpoint, altSlug }) {
    const params = useParams();
    const slug = params.slug ? params.slug : altSlug;

    const [content, setContent] = useState(null);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [progress, setProgress] = useState({});
    const [showEditFileNameDialog, setShowEditFileNameDialog] = useState(false);
    const [editedFile, setEditedFile] = useState(null);
    const [deleteConfirmFileIndex, setDeleteConfirmFileIndex] = useState(null);
    const [previewDialog, setPreviewDialog] = useState({
        open: false,
        url: "",
        fileName: "",
        filePath: "",
        kind: "unsupported",
        loading: false,
        error: "",
    });
    const previewObjectUrlRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user && user.is_staff === true;
    const previewNotAvailableText = t("buttonCommons.previewNotAvailable", {
        defaultValue: "Preview is not available for this file type.",
    });
    const previewLoadErrorText = t("buttonCommons.previewLoadError", {
        defaultValue: "Failed to load preview.",
    });

    const revokePreviewObjectUrl = () => {
        if (previewObjectUrlRef.current) {
            URL.revokeObjectURL(previewObjectUrlRef.current);
            previewObjectUrlRef.current = null;
        }
    };

    const TABLE_HEAD = [
        { id: "FileName", label: t('ContentDetail.AdminTable.headers.FileName'), alignRight: false, search: true },
        { id: "FileSize", label: t('ContentDetail.AdminTable.headers.FileSize'), alignRight: false, search: false },
        {
            id: "Preview",
            label: t('ContentDetail.AdminTable.headers.Preview'),
            alignRight: false,
            search: false,
        },
        {
            id: "Download",
            label: t('ContentDetail.AdminTable.headers.Download'),
            alignRight: false,
            search: false,
        },
    ];

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await get(dispatch, `${endpoint}${slug}`);
            setContent(res.data.content);
            dispatch(
                editContent({
                    slug: slug,
                    contentType: res.data.content.type,
                })
            );
            const orderedFiles = Array.isArray(res.data.files)
                ? [...res.data.files].sort((a, b) => {
                      const positionA =
                          typeof a.position === "number" ? a.position : 0;
                      const positionB =
                          typeof b.position === "number" ? b.position : 0;
                      return positionA - positionB;
                  })
                : [];
            setFiles(orderedFiles);
        } catch (err) {
            navigate("/404");
        } finally {
            setLoading(false);
        }
    }, [endpoint, navigate, slug, dispatch]);

    useEffect(() => {
        if (!content) {
            fetchData();
        }
    }, [content, fetchData]);

    useEffect(() => {
        return () => {
            dispatch(clearEditContent());
        };
    }, [dispatch]);

    const displayContentBody = () => {
        return { __html: content.body };
    };

    function urlToPromise(url) {
        return new Promise(function (resolve, reject) {
            JSZipUtils.getBinaryContent(url, function (err, data) {
                if (err) {
                    reject(err);
                } else {
                    resolve(data);
                }
            });
        });
    }

    const isImageFile = (fileName) => {
        if (!fileName) {
            return false;
        }
        const fileExt = fileName.split(".").pop();
        const imageExts = ["png", "jpg", "jpeg"];
        if (imageExts.indexOf(fileExt) >= 0) {
            return true;
        }
        return false;
    };

    const displayImages = () => {
        const images = [];
        for (const file of files) {
            if (isImageFile(file.fileName)) {
                images.push({
                    original: `${axios.defaults.baseURL}${file.filePath}`,
                });
            }
        }
        if (images.length > 0) {
            return (
                <ImageGallery items={images} additionalClass={Gallery.image} />
            );
        }
    };

    const downloadFile = async (filePath, fileName) => {
        saveAs(`${axios.defaults.baseURL}${filePath}`, fileName);
    };

    const resolveFileUrl = (filePath) => {
        if (!filePath) {
            return "";
        }
        if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
            return filePath;
        }
        return `${axios.defaults.baseURL}${filePath}`;
    };

    const resolvePreviewKind = (file) => {
        const fileName = (file.fileName || "").toLowerCase();
        const ext = fileName.includes(".")
            ? fileName.split(".").pop()
            : "";
        const type = (file.type || "").toLowerCase();
        const imageExts = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"];
        const videoExts = ["mp4", "webm", "ogg", "mov", "m4v"];

        if (imageExts.includes(ext) || type === "image" || type.startsWith("image/")) {
            return "image";
        }
        if (ext === "pdf" || type === "application/pdf") {
            return "pdf";
        }
        if (videoExts.includes(ext) || type === "video" || type.startsWith("video/")) {
            return "video";
        }
        return "unsupported";
    };

    const openPreviewDialog = async (file) => {
        const fileUrl = resolveFileUrl(file.filePath);
        if (!fileUrl) {
            return;
        }

        const kind = resolvePreviewKind(file);
        revokePreviewObjectUrl();
        setPreviewDialog({
            open: true,
            url: "",
            fileName: file.fileName || "",
            filePath: file.filePath || "",
            kind,
            loading: kind !== "unsupported",
            error: "",
        });

        if (kind === "unsupported") {
            return;
        }

        try {
            const response = await axios.get(fileUrl, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(response.data);
            previewObjectUrlRef.current = objectUrl;
            setPreviewDialog((prev) => ({
                ...prev,
                url: objectUrl,
                loading: false,
                error: "",
            }));
        } catch (err) {
            setPreviewDialog((prev) => ({
                ...prev,
                loading: false,
                error: previewLoadErrorText,
            }));
        }
    };

    useEffect(() => {
        return () => {
            revokePreviewObjectUrl();
        };
    }, []);

    const closePreviewDialog = () => {
        revokePreviewObjectUrl();
        setPreviewDialog({
            open: false,
            url: "",
            fileName: "",
            filePath: "",
            kind: "unsupported",
            loading: false,
            error: "",
        });
    };

    const renderPreviewContent = () => {
        if (previewDialog.loading) {
            return (
                <Typography variant="body2">
                    {t("buttonCommons.loading", { defaultValue: "Loading..." })}
                </Typography>
            );
        }
        if (previewDialog.error) {
            return (
                <Typography variant="body2">
                    {previewDialog.error}
                </Typography>
            );
        }
        if (!previewDialog.url && previewDialog.kind !== "unsupported") {
            return null;
        }
        if (previewDialog.kind === "unsupported") {
            return (
                <Typography variant="body2">
                    {previewNotAvailableText}
                </Typography>
            );
        }
        if (previewDialog.kind === "image") {
            return (
                <Box
                    component="img"
                    src={previewDialog.url}
                    alt={previewDialog.fileName}
                    sx={{
                        width: "100%",
                        maxHeight: "70vh",
                        objectFit: "contain",
                    }}
                />
            );
        }
        if (previewDialog.kind === "pdf") {
            return (
                <Box
                    component="iframe"
                    src={previewDialog.url}
                    title={previewDialog.fileName}
                    sx={{
                        width: "100%",
                        height: "70vh",
                        border: 0,
                    }}
                />
            );
        }
        if (previewDialog.kind === "video") {
            return (
                <Box
                    component="video"
                    src={previewDialog.url}
                    controls
                    sx={{
                        width: "100%",
                        maxHeight: "70vh",
                    }}
                />
            );
        }
        return (
            <Typography variant="body2">
                {previewNotAvailableText}
            </Typography>
        );
    };

    const onDownloadClicked = async (downloadOption) => {
        const filesToDownload = [];
        for (const file of files) {
            const { filePath, fileName } = file;
            const isItemSelected = selected.indexOf(fileName) !== -1;
            if (downloadOption === "all" || isItemSelected) {
                filesToDownload.push({
                    fileName: fileName,
                    filePath: filePath,
                });
            }
        }
        if (filesToDownload.length === 1) {
            downloadFile(
                filesToDownload[0].filePath,
                filesToDownload[0].fileName
            );
        } else {
            const zip = require("jszip")();

            const zipBaseName =
                (content && content.slug) ||
                slug ||
                t("ContentDetail.attachmentsFileName", {
                    defaultValue: "attachments",
                });
            const zipFilename = `${zipBaseName}.zip`;
            for (const file of filesToDownload) {
                const url = `${axios.defaults.baseURL}${file.filePath}`;
                const filename = file.fileName;
                zip.file(filename, urlToPromise(url), { binary: true });
            }

            setProgress({
                show: true,
                value: 0,
            });
            // when everything has been downloaded, we can trigger the dl
            zip.generateAsync(
                { type: "blob" },
                function updateCallback(metadata) {
                    setProgress({
                        show: true,
                        value: metadata.percent.toFixed(2),
                    });
                }
            ).then(
                function callback(blob) {
                    // see FileSaver.js
                    saveAs(blob, zipFilename);
                    setProgress({
                        show: false,
                        value: 0,
                    });
                },
                function (e) {
                    console.log(e);
                }
            );
        }
    };

    const submitAttachmentChanges = async (
        {
            removedFiles = [],
            modifiedFiles = [],
            uploadedFiles = [],
        },
        options = {}
    ) => {
        const { refresh = true, onError } = options;
        if (!content) {
            return false;
        }
        const payload = {
            data: {
                id: content.id,
                type: content.type,
                title: content.title,
                body: content.body,
                status: content.status,
                news: content.news,
                sticky: content.sticky,
            },
            removedFiles,
            modifiedFiles,
            uploadedFiles,
        };
        try {
            await post(dispatch, `/api/content/admin/edit/`, payload);
            dispatch(
                showAlert({
                    message: t("AdminContent.update.success"),
                    severity: "success",
                })
            );
            if (refresh) {
                await fetchData();
            }
            return true;
        } catch (err) {
            if (typeof onError === "function") {
                onError();
            }
            dispatch(
                showAlert({
                    message:
                        (err.response &&
                            err.response.data &&
                            (err.response.data.nav || err.response.data.error)) ||
                        err.message ||
                        t("AdminContent.update.error"),
                    severity: "error",
                })
            );
            return false;
        }
    };

    const options =
        selected.length > 0
            ? [
                  {
                      name: "all",
                      label: t('AdminTableCommons.contextMenuOptions.downloadAll'),
                      handler: onDownloadClicked,
                  },
                  {
                      name: "selected",
                      label: t('AdminTableCommons.contextMenuOptions.downloadSelected'),
                      handler: onDownloadClicked,
                  },
                  {
                      name: "clear",
                      label: t('AdminTableCommons.contextMenuOptions.clearSelection'),
                      handler: () => {
                          setSelected([]);
                      },
                  },
              ]
            : [
                  {
                      name: "all",
                      label: t('AdminTableCommons.contextMenuOptions.downloadAll'),
                      handler: onDownloadClicked,
                  },
              ];

    const editFileName = (index) => {
        setEditedFile(files[index]);
        setShowEditFileNameDialog(true);
    };

    const deleteFile = (index) => {
        const file = files[index];
        if (!file) {
            return;
        }
        submitAttachmentChanges({
            removedFiles: [file.id],
        });
    };

    const openDeleteFileConfirm = (index) => {
        setDeleteConfirmFileIndex(index);
    };

    const closeDeleteFileConfirm = () => {
        setDeleteConfirmFileIndex(null);
    };

    const submitDeleteFileConfirm = () => {
        if (deleteConfirmFileIndex == null) {
            return;
        }
        deleteFile(deleteConfirmFileIndex);
        setDeleteConfirmFileIndex(null);
    };

    const handleFileNameChanged = (value) => {
        if (!editedFile) {
            return;
        }
        submitAttachmentChanges({
            modifiedFiles: [{ Id: editedFile.id, FileName: value }],
        });
        setEditedFile(null);
    };

    const handleAttachmentRowsReorder = (reorderedRows) => {
        if (!isAdmin || !Array.isArray(reorderedRows)) {
            return;
        }
        const orderedIds = reorderedRows.map((row) => row.Id);
        if (!orderedIds.length || orderedIds.length !== files.length) {
            return;
        }
        const idToFile = new Map(files.map((file) => [file.id, file]));
        const reorderedFiles = orderedIds
            .map((id) => idToFile.get(id))
            .filter(Boolean);
        if (reorderedFiles.length !== files.length) {
            return;
        }
        const isSameOrder = reorderedFiles.every(
            (file, index) => file.id === files[index].id
        );
        if (isSameOrder) {
            return;
        }
        const withPosition = reorderedFiles.map((file, index) => ({
            ...file,
            position: index,
        }));
        const previousFiles = files;
        setFiles(withPosition);
        submitAttachmentChanges({
            modifiedFiles: withPosition.map((file, index) => ({
                Id: file.id,
                Position: index,
            })),
        }, {
            refresh: false,
            onError: () => setFiles(previousFiles),
        });
    };

    const uploadFile = async (e) => {
        const filesToUpload = Array.from(e.target.files);
        if (!filesToUpload.length) {
            return;
        }
        const toBase64 = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });

        try {
            const uploadedFiles = [];
            for (const [index, file] of filesToUpload.entries()) {
                const name = file.name;
                const size = file.size;
                const type = file.type;
                const base64File = await toBase64(file);
                uploadedFiles.push({
                    name,
                    size,
                    type,
                    file: base64File,
                    position: files.length + index,
                });
            }
            await submitAttachmentChanges({ uploadedFiles });
        } catch (err) {
            dispatch(
                showAlert({
                    message: err.message || t("AdminContent.update.error"),
                    severity: "error",
                })
            );
        } finally {
            e.target.value = "";
        }
    };

    const displayAttachments = () => {
        if (files && files.length > 0) {
            let rows = [];
            for (const file of files) {
                const { id, fileName, fileSize, filePath } = file;
                const previewUrl = resolveFileUrl(filePath);
                rows.push({
                    Id: id,
                    FileName: fileName,
                    FileSize: filesize(fileSize),
                    Preview: (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                openPreviewDialog(file);
                            }}
                            startIcon={<VisibilityOutlinedIcon />}
                            disabled={!previewUrl}
                        >
                            {t('buttonCommons.preview')}
                        </Button>
                    ),
                    Download: (
                        <Button
                            variant="contained"
                            onClick={() => {
                                downloadFile(filePath, fileName);
                            }}
                            startIcon={<Icon icon={downIcon} />}
                        >
                            {t('buttonCommons.download')}
                        </Button>
                    ),
                });
            }
            return (
                <Box mt={2} sx={{ height: "100%", width: "100%" }}>
                    <AdminTable
                        searchPlaceholder={t('AdminTableCommons.searchPlaceholder')}
                        headers={TABLE_HEAD}
                        rows={rows}
                        selection={{
                            selected: selected,
                            setSelected: setSelected,
                        }}
                        progress={progress}
                        rowDrag={
                            isAdmin
                                ? {
                                      enabled: true,
                                      disableWhenFiltered: true,
                                      idField: "Id",
                                      onReorder: handleAttachmentRowsReorder,
                                  }
                                : undefined
                        }
                        contextMenuOptions={
                            isAdmin
                                ? [
                                      {
                                          label: t('AttachmentsStep.AdminTable.contextMenuOptions.editFileName'),
                                          icon: <EditOutlinedIcon />,
                                          handler: (id) => {
                                              editFileName(id);
                                          },
                                      },
                                       {
                                           label: t('AttachmentsStep.AdminTable.contextMenuOptions.deleteFile'),
                                           icon: <DeleteOutlineOutlinedIcon />,
                                           handler: (id) => {
                                              openDeleteFileConfirm(id);
                                           },
                                       },
                                   ]
                                : undefined
                        }
                    >
                        <DownloadButton options={options} />
                        {isAdmin && (
                            <label htmlFor="upload-file-inline">
                                <input
                                    style={{ display: "none" }}
                                    id="upload-file-inline"
                                    name="upload-file-inline"
                                    type="file"
                                    onChange={uploadFile}
                                    multiple
                                />
                                <Fab
                                    color="primary"
                                    size="small"
                                    component="span"
                                    aria-label="add"
                                    variant="extended"
                                >
                                    <UploadFileIcon />
                                    &nbsp;{t('AttachmentsStep.uploadButton.label')}
                                </Fab>
                            </label>
                        )}
                    </AdminTable>
                </Box>
            );
        }
    };

    return (
        <>
            <InputTextDialog
                title={t('AttachmentsStep.InputTextDialog.title')}
                initialValue={editedFile ? editedFile.fileName : ""}
                open={showEditFileNameDialog}
                onClose={() => {
                    setShowEditFileNameDialog(false);
                }}
                onSubmit={handleFileNameChanged}
                validationList={files
                    .filter((file) => {
                        if (editedFile) {
                            return editedFile.fileName !== file.fileName;
                        }
                        return true;
                    })
                    .map((file) => file.fileName)}
                errorText={{
                    empty: t('AttachmentsStep.InputTextDialog.errorText.empty'),
                    alreadyExists: t('AttachmentsStep.InputTextDialog.errorText.alreadyExists')
                }}
            />
            <ConfirmDialog
                open={deleteConfirmFileIndex != null}
                onClose={closeDeleteFileConfirm}
                onSubmit={submitDeleteFileConfirm}
                title={t("ConfirmDialog.deleteTitle", {
                    defaultValue: "Confirm deletion",
                })}
                text={t("ConfirmDialog.deleteAttachmentText", {
                    defaultValue:
                        "Do you really want to remove selected attachment(s)? This action cannot be undone.",
                })}
                confirmLabel={t("buttonCommons.delete")}
                confirmColor="error"
            />
            <Dialog
                open={previewDialog.open}
                onClose={closePreviewDialog}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>{previewDialog.fileName || t("buttonCommons.preview")}</DialogTitle>
                <DialogContent dividers>
                    {renderPreviewContent()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePreviewDialog}>
                        {t("buttonCommons.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() =>
                            downloadFile(previewDialog.filePath, previewDialog.fileName)
                        }
                        disabled={!previewDialog.filePath}
                    >
                        {t("buttonCommons.download")}
                    </Button>
                </DialogActions>
            </Dialog>
            {loading ? (
                <Loader />
            ) : (
                <Page title={`${content.title}`}>
                    <Container maxWidth={false}>
                        <Box mb={2}>
                            <Button
                                variant="contained"
                                to=""
                                onClick={() => navigate(-1)}
                                startIcon={<Icon icon={backIcon} />}
                            >
                                {t('buttonCommons.back')}
                            </Button>
                        </Box>
                        <Typography variant="h4" sx={{ mb: 5 }}>
                            {content.title}
                        </Typography>

                        <div
                            className="mt-5 mb-5"
                            dangerouslySetInnerHTML={displayContentBody()}
                        />
                        <Stack
                            sx={{
                                mt: 4,
                                width: "100%",
                            }}
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                        >
                            {displayImages()}
                            {displayAttachments()}
                        </Stack>
                    </Container>
                </Page>
            )}
        </>
    );
}
